import * as React from "react";
import { FunctionComponent, useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input/input';
import { isValidPhoneNumber, formatPhoneNumber, isPossiblePhoneNumber } from "react-phone-number-input";
import { ClaimPackage } from "../../Models/ClaimPackage";
import { GeneralLiabilityClaimReport, ClaimType, Claim } from "../../Models/Claim";
import { CSSTransition } from 'react-transition-group';
import Server from "../../Server";
import { ClaimSuccess } from "../ClaimSuccess";
import FileDragAndDrop from "../FileDragAndDrop";
import { FilesTable } from "../FilesTable";
import ReactTooltip from 'react-tooltip';
import { ByteLimit, AfterToolTipHide, AfterToolTipShow, CloneAutoClaim, CloneGeneralLiabilityClaim, FixDate, PrettyPrintDate } from "../../Utils";
import { ProgressBar } from "react-bootstrap";

import { StepOne } from "./GeneralLiabilityClaimSteps/StepOne";
import { StepTwo } from "./GeneralLiabilityClaimSteps/StepTwo";
import { StepThree } from "./GeneralLiabilityClaimSteps/StepThree";
import { StepFour } from "./GeneralLiabilityClaimSteps/StepFour";
import { ValidateEmail } from "../../Utils";

interface GeneralLiabilityClaimProps {
	isQuincy: boolean;
	claimPackage: ClaimPackage;
	progressBack: () => void;
}

export const GeneralLiabilityClaim: FunctionComponent<GeneralLiabilityClaimProps> = ({ isQuincy, claimPackage, progressBack }) => {
	const [showStart, setShowStart] = useState<boolean>(true);
	const [width, setWidth] = useState<number>(window.innerWidth);
	const [currentStep, setCurrentStep] = useState<number>(2);
	const [showError, setShowError] = useState<boolean>(false);
	const [claim, setClaim] = useState<GeneralLiabilityClaimReport>(getNewClaim());
	const [showStepOne, setShowStepOne] = useState<boolean>(true);
	const [showStepTwo, setShowStepTwo] = useState<boolean>(false);
	const [showStepThree, setShowStepThree] = useState<boolean>(false);
	const [showStepFour, setShowStepFour] = useState<boolean>(false);
	const [showSuccess, setShowSuccess] = useState<boolean>(false);

	const [doGoBackCallback, setDoGoBackCallback] = useState<boolean>(false);
	const [doGoBackCallbackStep, setDoGoBackCallbackStep] = useState<number>(-1);
	const [stepFourSubmit, setStepFourSubmit] = useState<boolean>(false);

	const [stepTwoError, setStepTwoError] = useState<boolean>(false);
	const [stepThreeError, setStepThreeError] = useState<boolean>(false);
	const [stepFourError, setStepFourError] = useState<boolean>(false);
	const [stepFiveError, setStepFiveError] = useState<boolean>(false);

	const [showToolTip, setShowToolTip] = useState<boolean>(false);
	const [files, setFiles] = useState<File[]>([]);
	const [uploadLimitBreached, setShowUploadLimitBreach] = useState<boolean>(false);
	const [fileSize, setFileSize] = useState<number>(0);
	const [progress, setProgress] = useState<number>(0);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [responseError, setResponseError] = useState<string>("");

	let toolTipRef: any = null;
	function getNewClaim() {
		var claim = new GeneralLiabilityClaimReport();
		claim.claimNumber = claimPackage.claimNumber;
		claim.policyNumber = claimPackage.policyNumber;
		claim.lossDate = claimPackage.lossDate;
		claim.type = ClaimType.GeneralLiability;
		claim.websiteId = window["websiteId"];
		return claim;
	}

	function handleWindowSizeChange() {
		setWidth(window.innerWidth);
	}

	useEffect(() => {
		window.addEventListener('resize', handleWindowSizeChange);
		return () => {
			window.removeEventListener('resize', handleWindowSizeChange);
		}
	}, []);

	let isMobile: boolean = (width <= 768);

	function setUploadProgress(progress: number) {
		setProgress(progress);
	}

	function fileExists(fileName: string) {
		let matches = files.find((file: File, i: number) => {
			return file.name.toLocaleLowerCase() === fileName.toLocaleLowerCase();
		})
		return matches;
	}

	function handleDrop(newFiles: FileList) {
		let fileList: File[] = [...files as []];
		let currentFileSizes = 0;
		let finalSize = fileSize;
		for (let i = 0; i < newFiles.length; i++) {
			let file = newFiles[i] as File;
			if (fileExists(file.name))
				continue;
			fileList.push(file);
			currentFileSizes += file.size;
		}
		finalSize += currentFileSizes;
		setFiles(fileList);
		setFileSize(finalSize);
		if (finalSize > ByteLimit)
			setShowUploadLimitBreach(true);
	}

	function deleteFile(index: number) {
		let fileList: File[] = [...files as []];
		let file = files![index];
		let newSize = fileSize - file.size;
		fileList.splice(index, 1);
		if (newSize < ByteLimit)
			setShowUploadLimitBreach(false);
		setFileSize(newSize);
		setFiles(fileList);
	}

	function getLearnMore() {
		return `<p id='learn-more-tip' class='upload-tip' role='tooltip'>9MB is approximately 3 photos on a modern smartphone or tablet.  This size varies by your image quality settings and device camera. When taking photos please avoid the RAW file format as the file size exponentially increases and you will
        not be able to include these.  Disabling RAW photos varies by Android or iOS so consult your camera app's settings.</p>`;
	}

	function handleToolTip() {
		if (showToolTip) {
			ReactTooltip.hide(toolTipRef);
			setShowToolTip(false);
		}
		else {
			ReactTooltip.show(toolTipRef);
			setShowToolTip(true);
		}
	}

	function goBack() {
		progressBack();
	}

	function goToStep(step: number, claim: GeneralLiabilityClaimReport, revalidate: boolean = false) {
		console.log("Going to step " + step);
		setCurrentStep(step);
		setClaim(claim);
		if (step === 2) {
			setDoGoBackCallback(false);
			setDoGoBackCallbackStep(-1);
			setShowStepOne(true);
			setShowStepTwo(false);
			setShowStepThree(false);
			setShowStepFour(false);
		}
		else if (step === 3) {
			setDoGoBackCallback(false);
			setDoGoBackCallbackStep(-1);
			setShowStepOne(false);
			setShowStepTwo(true);
			setShowStepThree(false);
			setShowStepFour(false);
		}
		else if (step === 4) {
			setDoGoBackCallback(false);
			setDoGoBackCallbackStep(-1);
			setShowStepOne(false);
			setShowStepTwo(false);
			setShowStepThree(true);
			setShowStepFour(false);
		}
		else if (step === 5) {
			setDoGoBackCallback(false);
			setDoGoBackCallbackStep(-1);
			setShowStepOne(false);
			setShowStepTwo(false);
			setShowStepThree(false);
			setShowStepFour(true);
		}
	}

	function claimSuccess() {
		setShowStart(false);
	}

	function setError(status: boolean) {
		setShowError(status);
	}

	return <div>
		<CSSTransition in={showStart} timeout={300}
			classNames="claim-transition"
			unmountOnExit
			onExited={() => { setShowSuccess(true); }}>
			<div className="auto-claim form-horizontal">
				<div>
					<h1>REPORT A CLAIM: GENERAL LIABILITY (step {currentStep} of 5)</h1>
					<h2 className="lossdate">Loss date of {PrettyPrintDate(claim.lossDate)}</h2>
					<p><strong><span className="red-ast">*</span> denotes required field(s)</strong></p>
				</div>
				<CSSTransition in={showStepOne} timeout={300}
					classNames="claim-transition"
					unmountOnExit>
					<StepOne claim={claim} progressBack={goBack} nextStep={goToStep} showError={showError} />
				</CSSTransition>
				<CSSTransition in={showStepTwo} timeout={300}
					classNames="claim-transition"
					unmountOnExit>
					<StepTwo claim={claim} nextStep={goToStep} showError={showError} />
				</CSSTransition>
				<CSSTransition in={showStepThree} timeout={300}
					classNames="claim-transition"
					unmountOnExit>
					<StepThree claim={claim} goBack={goToStep} showError={showError} />
				</CSSTransition>
				<CSSTransition in={showStepFour} timeout={300}
					classNames="claim-transition"
					unmountOnExit>
					<StepFour claim={claim} showError={showError} doGoBackCallback={doGoBackCallback} goBackCallbackStep={doGoBackCallbackStep}
						doSubmit={stepFourSubmit} goBack={goToStep} submit={submitClaim} />
				</CSSTransition>
				{
					currentStep === 5 ?
						<div>
							<div className="form-group">
								<p className="force-left col-lg-12 col-sm-12 col-xs-12 control-label">Would you like to upload a photo or PDF file to attach to your claim?</p>
							</div>
							<div className="form-group uploader">
								<div className="col-lg-4 col-md-6 col-sm-6 col-xs-6 uploader-button-container">
									<label htmlFor="claim-uploads" className="btn btn-primary uploader-button"><i className="fa fa-paperclip" />&nbsp;Browse Files
										<input id="claim-uploads" className="file-uploader" aria-describedby="fileUploadError" type="file" multiple onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											handleDrop(e.target.files as FileList);
										}} /></label>

								</div>
								<div className="col-lg-8 col-md-6 col-sm-6 col-xs-6 learn-more">
									<p>Using <span className={uploadLimitBreached ? "error" : ""}>{fileSize < 10000 ? (fileSize / 1024 / 1024).toFixed(3) : (fileSize / 1024 / 1024).toFixed(2)}MB</span> out of <strong>9MB</strong> limit.
										<button className="learn-more-link" ref={ref => toolTipRef = ref} type="button" onClick={() => { handleToolTip(); }} data-tip={getLearnMore()}>Learn More</button>
									</p>
									{/*@ts-ignore */}
									<ReactTooltip afterHide={AfterToolTipHide} afterShow={AfterToolTipShow} className="limitTooltip opaque" offset={{ left: 50 }} clickable={true} place="top" type="info" effect="solid" html={true} />
								</div>
							</div>
							<p id="fileUploadError" role="alert" className={uploadLimitBreached ? "error" : "error-hidden"}>You are over the upload size limit.  Remove or choose other files.</p>
							<div className="col-lg-12">
								{isMobile ? <FilesTable files={files} deleteFile={deleteFile} /> : null}
								{isMobile ? null :
									<FileDragAndDrop handleDrop={handleDrop}>
										<div className="file-drag">
											<h2>Or drag files here</h2>
											<FilesTable files={files} deleteFile={deleteFile} />
										</div>
									</FileDragAndDrop>
								}
							</div>
							<div>
								{showError ? <div role="alert" aria-hidden={!showError} className="error">
									There are errors in your submissions.  Please check your information, correct any errors and re-submit again:
									<div className="steps-errors">
										{stepTwoError ? <button className="btn btn-danger" type="button" onClick={() => { stepFourGoBack(2); }}>Step 2</button> : null}
										{stepThreeError ? <button className="btn btn-danger" type="button" onClick={() => { stepFourGoBack(3); }}>Step 3</button> : null}
										{stepFourError ? <button className="btn btn-danger" type="button" onClick={() => { stepFourGoBack(4); }}>Step 4</button> : null}
										{stepFiveError ? <button className="btn btn-danger" type="button" onClick={() => { stepFourGoBack(5); }}>Step 5</button> : null}
									</div>
								</div> : null}
								<div aria-hidden={!submitting && responseError === ""} className="col-lg-12 col-md-12 col-sm-12 col-xs-12 progress-container">
									{files.length > 0 && submitting && progress !== 100 ? <h4>Uploading attachments for your claim, please wait...</h4> : null}
									{submitting && progress === 100 ? <h4>Submitting your claim, please wait...<i className="fa fa-spinner fa-spin"></i></h4> : null}
									{files.length > 0 && submitting && progress !== 0 ? <ProgressBar role="progressbar" aria-valuemin={0} aria-valuemax={100} striped={true} animated={true} aria-valuenow={progress} now={progress} label={`${progress}%`} /> : null}
									{responseError !== "" && !submitting ? <p role="alert" aria-hidden={responseError === "" && !submitting} className="error">We could not submit your claim.  {responseError}</p> : null}
								</div>
							</div>
							<div className="form-group button-group">
								<button className="btn btn-primary col-lg-6 col-sm-6 col-xs-6" type="button" disabled={submitting} onClick={() => { stepFourGoBack(4); }}><i className="fa fa-arrow-left" aria-hidden="true" />&nbsp;BACK</button>
								<button className="btn btn-primary col-lg-6 col-sm-6 col-xs-6" type="button" disabled={submitting} onClick={() => { stepFourSubmitClaim(); }}><i className="fa fa-upload" aria-hidden="true" />&nbsp;FINISH</button>
							</div>
						</div>
						: null
				}
			</div>
		</CSSTransition>
		<CSSTransition in={showSuccess} timeout={300}
			classNames="claim-transition"
			unmountOnExit
			onExited={() => setShowStart(true)}>
			<ClaimSuccess claimNumber={claimPackage.claimNumber} claimType={ClaimType.GeneralLiability} />
		</CSSTransition>
	</div>;

	function stepFourGoBack(step: number) {
		setDoGoBackCallbackStep(step);
		setDoGoBackCallback(true);
	}

	function stepFourSubmitClaim() {
		setStepFourSubmit(true);
	}

	//Essentially when a claim is sent out it will check each field of the object, if one is invalid then set a button to go back to the error and set showerror to true in the submit claim function. the errors wshould then highlight themselves.
	function validateClaim(claim: GeneralLiabilityClaimReport) {
		let valid = true;
		let claimRelation = claim.additionalAnswers["altContactRelation"];
		let bestContact = claim.additionalAnswers["bestContact"];
		let lawsuit = localStorage.getItem("generalclaim-lawsuit");
		let witnesses = localStorage.getItem("generalclaim-witnesses");
		let stepTwoError = false;
		let stepThreeError = false;
		let stepFourError = false;
		let stepFiveError = false;

		if (!claim.policyName || claim.policyName === "") {
			stepTwoError = true;
			valid = false;
		}
		if (claim.primaryPhone !== "" && !isValidPhoneNumber(claim.primaryPhone)) {
			stepTwoError = true;
			valid = false;
		}
		if (claim.altPhone !== "" && !isValidPhoneNumber(claim.altPhone)) {
			stepTwoError = true;
			valid = false;
		}
		if (!claimRelation || claimRelation === "") {
			stepTwoError = true;
			valid = false;
		}
		if (claim.additionalAnswers["altContactName"] === "") {
			stepTwoError = true;
			valid = false;
		}
		if (claim.additionalAnswers["altContactPhone"] === "") {
			stepTwoError = true;
			valid = false;
		}
		if (claim.additionalAnswers["altContactPhone"] !== "" && !isValidPhoneNumber(claim.additionalAnswers["altContactPhone"])) {
			stepTwoError = true;
			valid = false;
		}
		if (claim.additionalAnswers["altContactEmail"] === "" ||
			(claim.additionalAnswers["altContactEmail"] !== "" && !ValidateEmail(claim.additionalAnswers["altContactEmail"]))) {
			stepTwoError = true;
			valid = false;
		}
		if (localStorage.getItem("generalclaim-bestcontact") === "Other") {
			if (!claim.bestContactName || claim.bestContactName === "") {
				stepTwoError = true;
				valid = false;
			}
			if (!claim.bestContactPhone || claim.bestContactPhone === "") {
				stepTwoError = true;
				valid = false;
			}
			if (claim.bestContactPhone !== "" && claim.bestContactPhone && !isValidPhoneNumber(claim.bestContactPhone)) {
				stepTwoError = true;
				valid = false;
			}
			if (claim.bestContactAltPhone !== "" && claim.bestContactAltPhone && !isValidPhoneNumber(claim.bestContactAltPhone)) {
				stepTwoError = true;
				valid = false;
			}
		}

		if (!claim.explanation || claim.explanation === "") {
			stepThreeError = true;
			valid = false;
		}
		if (!claim.line1 || claim.line1 === "") {
			stepThreeError = true;
			valid = false;
		}
		if (!claim.city || claim.city === "") {
			stepThreeError = true;
			valid = false;
		}
		if (!claim.state || claim.state === "") {
			stepThreeError = true;
			valid = false;
		}
		if (!claim.zipCode || claim.zipCode === "") {
			stepThreeError = true;
			valid = false;
		}
		if (!lawsuit || lawsuit === "") {
			stepThreeError = true;
			valid = false;
		}

		if (!claim.occuranceType || claim.occuranceType === "") {
			stepFourError = true;
			valid = false;
		}
		if (claim.propertyOwnersPrimaryPhone !== "" && !isValidPhoneNumber(claim.propertyOwnersPrimaryPhone)) {
			stepFourError = true;
			valid = false;
		}
		if (claim.propertyOwnersAlternatePhone !== "" && !isValidPhoneNumber(claim.propertyOwnersAlternatePhone)) {
			stepFourError = true;
			valid = false;
		}
		if (claim.propertyOwnersEmail !== "" && !ValidateEmail(claim.propertyOwnersEmail)) {
			stepFourError = true;
			valid = false;
		}
		if (claim.injuredPersonsPrimaryPhone !== "" && !isValidPhoneNumber(claim.injuredPersonsPrimaryPhone)) {
			stepFourError = true;
			valid = false;
		}
		if (claim.injuredPersonsAlternatePhone !== "" && !isValidPhoneNumber(claim.injuredPersonsAlternatePhone)) {
			stepFourError = true;
			valid = false;
		}
		if (claim.injuredPersonsEmail !== "" && !ValidateEmail(claim.injuredPersonsEmail)) {
			stepFourError = true;
			valid = false;
		}

		if (!witnesses || witnesses === "") {
			stepFiveError = true;
			valid = false;
		}
		if (fileSize > ByteLimit) {
			stepFiveError = true;
			valid = false;
		}

		setStepTwoError(stepTwoError);
		setStepThreeError(stepThreeError);
		setStepFourError(stepFourError);
		setStepFiveError(stepFiveError);
		return valid;
	}

	
	function handleError(status: boolean) {
		setError(status);
	}

	function submitClaim(claim: GeneralLiabilityClaimReport) {
		console.log("Submitting general liability");
		setStepFourSubmit(false);
		if (submitting)
			return;

		let finalClaim = CloneGeneralLiabilityClaim(claim) as GeneralLiabilityClaimReport;
		let isValid = validateClaim(finalClaim);
		if (isValid) {
			finalClaim.altPhone = formatPhoneNumber(finalClaim.altPhone);
			finalClaim.primaryPhone = formatPhoneNumber(finalClaim.primaryPhone);
			finalClaim.bestContactPhone = formatPhoneNumber(finalClaim.bestContactPhone);
			finalClaim.bestContactAltPhone = formatPhoneNumber(finalClaim.bestContactAltPhone);
			finalClaim.propertyOwnersPrimaryPhone = formatPhoneNumber(finalClaim.propertyOwnersPrimaryPhone);
			finalClaim.propertyOwnersAlternatePhone = formatPhoneNumber(finalClaim.propertyOwnersAlternatePhone);
			finalClaim.injuredPersonsPrimaryPhone = formatPhoneNumber(finalClaim.injuredPersonsPrimaryPhone);
			finalClaim.injuredPersonsAlternatePhone = formatPhoneNumber(finalClaim.injuredPersonsAlternatePhone);
			finalClaim.witnessPrimaryPhone = formatPhoneNumber(finalClaim.witnessPrimaryPhone);
			finalClaim.witnessAlternatePhone = formatPhoneNumber(finalClaim.witnessAlternatePhone);
			if (finalClaim.additionalAnswers["altContactPhone"])
				finalClaim.additionalAnswers["altContactPhone"] = formatPhoneNumber(finalClaim.additionalAnswers["altContactPhone"]);
			handleError(false);
			setSubmitting(true);
			setShowUploadLimitBreach(false);
			Server.sendClaim(finalClaim, files || [] as any, "quincy/claim/submission/type/generalLiability", setUploadProgress).then((result: string) => {
				if (result) {
					if (result === "true") {
						setResponseError("");
						localStorage.removeItem("generallaim-bestcontact");
						localStorage.removeItem("generalclaim-lawsuit");
						localStorage.removeItem("generalclaim-witnesses");
						claimSuccess();
					}
					else if (!result.startsWith("<!DOCTYPE")) {
						setResponseError(result.replace(/"/g, '')); //error string from API
					}
					else setResponseError("A general system error occurred.  Please contact the system administrator.")
					setSubmitting(false);
				}
				else
					setResponseError("A general system error occurred.  Please contact the system administrator.");
				setSubmitting(false);

			});
		}
		else handleError(true);
	}

}
